import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { List, Skeleton } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import { StyledListItemContent } from 'theme/styled';
import { setTour } from 'store/tour/slice';
import { useAppDispatch } from 'store/hooks';
import {
  LegacyAppointment,
  fetchAppointments
} from 'services/api/appointments';

import { showRestError } from 'utils/showErrorMessage';
import AppointmentItem from './AppointmentItem';

const PAGE_LIMIT = 10;

interface IProps {
  id: string;
  setTotal(total: number): void;
}

const Appointments: FC<IProps> = ({ id, setTotal }) => {
  const page = useRef(1);
  const dispatch = useAppDispatch();

  const [results, setResults] = useState<LegacyAppointment[]>([]);
  const [total, setTotal_] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    (pageToLoad: number, append: boolean) => {
      setLoading(true);
      fetchAppointments({ patient: id, page: pageToLoad, limit: PAGE_LIMIT })
        .then(res => {
          setResults(prev =>
            append ? [...prev, ...res.results] : res.results
          );
          setTotal(res.total);
          setTotal_(res.total);
          dispatch(setTour({ key: 'patients', value: true }));
        })
        .catch(err => showRestError(err))
        .finally(() => setLoading(false));
    },
    [id, setTotal, dispatch]
  );

  useEffect(() => {
    page.current = 1;
    load(1, false);
  }, [load]);

  const loadMoreData = () => {
    page.current = page.current + 1;
    load(page.current, true);
  };

  return (
    <StyledListItemContent id="appointments-list">
      <InfiniteScroll
        dataLength={results.length}
        next={loadMoreData}
        hasMore={results.length < total}
        loader={
          <div className="list-content-item" style={{ marginTop: 16 }}>
            <Skeleton title={false} paragraph={{ rows: 2 }} active />
          </div>
        }
        scrollableTarget="appointments-list"
      >
        <List
          loading={loading}
          dataSource={results}
          renderItem={item => (
            <List.Item className="list-content-item">
              <AppointmentItem
                id={item.id}
                dates={item.dates}
                plan={item.plan}
                paid={item.paid}
                status={item.status}
              />
            </List.Item>
          )}
        />
      </InfiniteScroll>
    </StyledListItemContent>
  );
};

export default Appointments;
